import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { trackPageView, identifyUser } from '@/utils/mixpanel';
import { trackFunnelPage } from '@/utils/googleAnalytics';
import { useAuth } from '@/contexts/AuthContext';

// Map routes to friendly page names
const getPageName = (pathname: string): string => {
  const routes: Record<string, string> = {
    '/': 'Home',
    '/learn': 'Learn',
    '/services': 'Services',
    '/onboard': 'Onboarding',
    '/onboard/success': 'Onboard Success',
    '/pricing': 'Pricing',
    '/community': 'Community',
    '/team': 'Team',
    '/signin': 'Sign In',
    '/auth/callback': 'Auth Callback',
    '/app/home': 'Dashboard',
    '/settings': 'Account Settings',
    '/success': 'Payment Success',
    '/vibe-path': 'Vibe Path',
    
    
    '/vovy/chat': 'Vovy Chat',
    '/vovy/prompt': 'Prompt Labs',
    '/terms-of-service': 'Terms of Service',
    '/privacy-policy': 'Privacy Policy',
    '/posts': 'Blog',
  };

  // Check exact match first
  if (routes[pathname]) {
    return routes[pathname];
  }

  // Check for blog posts
  if (pathname.startsWith('/blog/')) {
    const slug = pathname.replace('/blog/', '');
    return `Blog - ${slug.split('-').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ')}`;
  }

  // Check for docs
  if (pathname.startsWith('/docs/')) {
    return `Docs - ${pathname.replace('/docs/', '')}`;
  }


  // Check for certificate
  if (pathname.startsWith('/certificate/')) {
    return 'Certificate';
  }

  // Check for demo
  if (pathname.startsWith('/demo/')) {
    return `Demo - ${pathname.replace('/demo/', '')}`;
  }


  // Default: capitalize pathname
  return pathname.split('/').filter(Boolean).map(segment =>
    segment.split('-').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ')
  ).join(' - ') || 'Unknown Page';
};

// Module-scoped so the guard survives RouteTracker remounts (the tree
// remounts when auth state settles, which resets any useRef).
let lastTrackedPath: string | null = null;

export const RouteTracker = () => {
  const location = useLocation();
  const { user } = useAuth();

  useEffect(() => {
    // Identify user if logged in
    if (user) {
      identifyUser(user.id);
    }

    if (lastTrackedPath === location.pathname) return;
    lastTrackedPath = location.pathname;

    // Track page view
    const pageName = getPageName(location.pathname);
    trackPageView(pageName, {
      pathname: location.pathname,
      search: location.search,
      hash: location.hash,
    });

    // GA4 funnel milestones (home → onboarding → signup → paywall → checkout → first unit)
    trackFunnelPage(location.pathname);
  }, [location, user]);

  return null; // This component doesn't render anything
};

export default RouteTracker;

